import React from 'react';
import { motion } from 'framer-motion';
import { 
  Users, Crown, Briefcase, Rocket, Star, TrendingUp, CreditCard, 
  Store, ShoppingBag, MessageCircle, AlertCircle, CheckCircle,
  ArrowUpRight, ArrowDownRight
} from 'lucide-react';

const C = {
  navy: '#1e3a5f',
  orange: '#f97316',
  green: '#22c55e',
};

// Composant carte statistique
const StatCard = ({ title, value, subtitle, icon: Icon, color, trend, trendValue }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm"
  >
    <div className="flex justify-between items-start mb-4">
      <div 
        className="w-12 h-12 rounded-xl flex items-center justify-center"
        style={{ background: `${color}15` }}
      >
        <Icon size={24} color={color} />
      </div>
      {trend && (
        <div className={`flex items-center gap-1 text-sm font-semibold ${trend === 'up' ? 'text-green-600' : 'text-red-500'}`}>
          {trend === 'up' ? <ArrowUpRight size={16} /> : <ArrowDownRight size={16} />}
          {trendValue}
        </div>
      )}
    </div>
    <div className="text-3xl font-bold text-gray-900 mb-1">{value}</div>
    <div className="text-sm text-gray-500">{title}</div>
    {subtitle && <div className="text-xs text-gray-400 mt-1">{subtitle}</div>}
  </motion.div>
);

// Composant activité récente
const ActivityItem = ({ icon: Icon, iconColor, title, subtitle, time }) => (
  <div className="flex items-start gap-3 p-3 hover:bg-gray-50 rounded-xl transition-colors">
    <div 
      className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
      style={{ background: `${iconColor}15` }}
    >
      <Icon size={18} color={iconColor} />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium text-gray-900 truncate">{title}</p>
      <p className="text-xs text-gray-500">{subtitle}</p>
    </div>
    <span className="text-xs text-gray-400 flex-shrink-0">{time}</span>
  </div>
);

export default function PlateformeDashboardPage() {
  // Données de démonstration
  const stats = {
    totalUsers: 1247,
    activeSubscriptions: 892,
    mrr: 45680,
    arr: 548160,
    newUsersThisMonth: 87,
    churnRate: 2.1,
  };

  const subscriptionBreakdown = [
    { plan: 'Gratuit', count: 423, color: C.green, icon: Star },
    { plan: 'Pro', count: 312, color: '#3B82F6', icon: Briefcase },
    { plan: 'Business', count: 198, color: C.orange, icon: Rocket },
    { plan: 'Premium', count: 67, color: C.navy, icon: Crown },
  ];

  const recentActivity = [
    { icon: Crown, iconColor: C.navy, title: 'Dupont Plomberie', subtitle: 'Passage en Premium', time: 'Il y a 2h' },
    { icon: Store, iconColor: C.orange, title: 'Martin Électricité', subtitle: 'Nouvelle boutique créée', time: 'Il y a 3h' },
    { icon: Users, iconColor: '#3B82F6', title: 'Bernard Carrelage', subtitle: 'Nouvel abonnement Pro', time: 'Il y a 5h' },
    { icon: MessageCircle, iconColor: C.green, title: 'Ticket #1234', subtitle: 'Nouveau ticket support', time: 'Il y a 6h' },
    { icon: ShoppingBag, iconColor: '#8B5CF6', title: 'Richard Chauffage', subtitle: '50 nouveaux produits ajoutés', time: 'Il y a 8h' },
  ];

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center">
            <Crown size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Tableau de bord Plateforme</h1>
            <p className="text-sm text-gray-500">Vue d'ensemble de AbracadaWorld</p>
          </div>
        </div>
      </div>

      {/* Stats principales */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          title="Utilisateurs totaux"
          value={stats.totalUsers.toLocaleString()}
          icon={Users}
          color={C.navy}
          trend="up"
          trendValue="+12%"
        />
        <StatCard
          title="Abonnements actifs"
          value={stats.activeSubscriptions.toLocaleString()}
          icon={CheckCircle}
          color={C.green}
          trend="up"
          trendValue="+8%"
        />
        <StatCard
          title="MRR"
          value={`${stats.mrr.toLocaleString()}€`}
          subtitle={`ARR: ${stats.arr.toLocaleString()}€`}
          icon={CreditCard}
          color={C.orange}
          trend="up"
          trendValue="+15%"
        />
        <StatCard
          title="Nouveaux ce mois"
          value={stats.newUsersThisMonth}
          subtitle={`Churn: ${stats.churnRate}%`}
          icon={TrendingUp}
          color="#8B5CF6"
          trend="up"
          trendValue="+23%"
        />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Répartition des abonnements */}
        <div className="lg:col-span-1 bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Répartition des abonnements</h3>
          <div className="space-y-4">
            {subscriptionBreakdown.map((item, i) => (
              <div key={i} className="flex items-center gap-3">
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ background: `${item.color}15` }}
                >
                  <item.icon size={18} color={item.color} />
                </div>
                <div className="flex-1">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm font-medium text-gray-900">{item.plan}</span>
                    <span className="text-sm font-bold" style={{ color: item.color }}>{item.count}</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full rounded-full transition-all duration-500"
                      style={{ 
                        width: `${(item.count / stats.activeSubscriptions) * 100}%`,
                        background: item.color 
                      }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Activité récente */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold text-gray-900">Activité récente</h3>
            <button className="text-sm text-blue-600 hover:text-blue-700 font-medium">
              Voir tout
            </button>
          </div>
          <div className="space-y-1">
            {recentActivity.map((activity, i) => (
              <ActivityItem key={i} {...activity} />
            ))}
          </div>
        </div>
      </div>

      {/* Alertes / Actions rapides */}
      <div className="mt-6 bg-gradient-to-r from-orange-50 to-amber-50 rounded-2xl border border-orange-100 p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <AlertCircle size={24} className="text-orange-600" />
          </div>
          <div>
            <h4 className="font-bold text-gray-900 mb-1">Actions en attente</h4>
            <p className="text-sm text-gray-600 mb-3">
              5 tickets support non résolus • 3 abonnements en attente de validation • 2 boutiques à modérer
            </p>
            <div className="flex gap-3">
              <button className="px-4 py-2 bg-orange-500 text-white text-sm font-semibold rounded-lg hover:bg-orange-600 transition-colors">
                Voir les tickets
              </button>
              <button className="px-4 py-2 bg-white text-orange-600 text-sm font-semibold rounded-lg border border-orange-200 hover:bg-orange-50 transition-colors">
                Valider les abonnements
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
